/**
 * Stats from games played on this device. Only completed games are counted;
 * games started but not finished appear as a separate note.
 */
import { LEVELS, type Level } from './types.ts';

/** Saved when a game's board is first shown. */
export interface StartRecord {
  level: Level;
  puzzleId: string;
  startedAt: string;
}

/** Saved when a game is solved. */
export interface SolveRecord extends StartRecord {
  elapsedMs: number;
  checksUsed: number;
  hintsUsed: number;
  autoNotesUsed: boolean;
  finishedAt: string;
}

export interface TimeStats {
  solved: number;
  bestMs: number | null;
  averageMs: number | null;
}

export interface LevelStats {
  level: Level;
  withoutAutoNotes: TimeStats;
  withAutoNotes: TimeStats;
  solved: number;
  /** Per completed game, both groups together. Null when nothing is solved yet. */
  averageHints: number | null;
  averageChecks: number | null;
  /** Started (board shown) but not solved, including games still in progress. */
  unfinished: number;
}

const sameGame = (a: StartRecord, b: StartRecord) => a.puzzleId === b.puzzleId && a.startedAt === b.startedAt;

function timeStats(solves: SolveRecord[]): TimeStats {
  if (!solves.length) return { solved: 0, bestMs: null, averageMs: null };
  const times = solves.map((s) => s.elapsedMs);
  return {
    solved: solves.length,
    bestMs: Math.min(...times),
    averageMs: times.reduce((a, b) => a + b, 0) / times.length,
  };
}

const average = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

export function computeStats(solves: SolveRecord[], starts: StartRecord[]): LevelStats[] {
  return LEVELS.map((level) => {
    const mine = solves.filter((s) => s.level === level);
    return {
      level,
      withoutAutoNotes: timeStats(mine.filter((s) => !s.autoNotesUsed)),
      withAutoNotes: timeStats(mine.filter((s) => s.autoNotesUsed)),
      solved: mine.length,
      averageHints: average(mine.map((s) => s.hintsUsed)),
      averageChecks: average(mine.map((s) => s.checksUsed)),
      unfinished: starts.filter((st) => st.level === level && !mine.some((s) => sameGame(s, st))).length,
    };
  });
}

/** "1.4", "0", "2" — one decimal, trailing ".0" dropped. */
export const formatAverage = (n: number) => String(Math.round(n * 10) / 10);
